import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { safariTips } from '../../../shared/components/ui/LoadingState.messages';
import { GoogleOAuthButton } from './GoogleOAuthButton';

function TipsCarousel() {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => setIdx((i: number) => (i + 1) % safariTips.length), 8000);
    return () => clearInterval(timer);
  }, []);
  // Split emoji and text if present (emoji at start)
  const tipRaw = safariTips[idx];
  const match = tipRaw.match(/^([\p{Emoji}\uD83D-\uDBFF\uDC00-\uDFFF\u2600-\u27BF]+)\s*(.*)$/u);
  const emoji = match ? match[1] : '';
  const text = match ? match[2] : tipRaw;

  // Dots logic: never show more than 3 dots
  let dots: number[] = [];
  if (safariTips.length <= 3) {
    dots = Array.from({ length: safariTips.length }, (_, i) => i);
  } else {
    // Always show 3 dots: prev, current, next (wrap around)
    const prev = (idx - 1 + safariTips.length) % safariTips.length;
    const next = (idx + 1) % safariTips.length;
    dots = [prev, idx, next];
  }

  return (
    <div className="text-center min-h-[80px] flex flex-col items-center" style={{minHeight: 128}}>
      {emoji && <span className="text-3xl mb-1" aria-hidden>{emoji}</span>}
      <p className="text-base font-medium text-gray-700 mb-1 animate-fade-in" style={{minHeight: 38}}>{text}</p>
      <div className="flex gap-1 justify-center mt-2">
        {dots.map((tipIdx: number, i: number) => (
          <span key={i} className={`inline-block w-2 h-2 rounded-full ${tipIdx === idx ? 'bg-amber-400' : 'bg-gray-300'}`}></span>
        ))}
      </div>
    </div>
  );
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const from = location.state?.from || '/dashboard';
  const message = location.state?.message;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password, formData.rememberMe);
    } catch (error) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-50 px-4 sm:px-6">
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md lg:max-w-4xl mx-auto">
        <div className="flex flex-col lg:flex-row w-full bg-white rounded-lg lg:rounded-xl shadow-xl overflow-hidden">
          {/* Tips panel for large screens */}
          <div className="hidden lg:flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-amber-50 px-8 py-8 w-1/2">
            <a href="#" className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
              <img className="w-8 h-8 mr-2" src="https://flowbite.s3.amazonaws.com/blocks/marketing-ui/logo.svg" alt="logo" />
              Dala Rides    
            </a>
            <TipsCarousel />
          </div>
          <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-6">
            <div className="flex flex-col w-full max-w-md">
              <div className="flex items-center gap-3 mb-4 justify-center lg:hidden">
                <a href="#" className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                  <img className="w-6 h-6 mr-2" src="https://flowbite.s3.amazonaws.com/blocks/marketing-ui/logo.svg" alt="logo" />  
                  Dala Rides    
                </a>
              </div>
              <div className="text-center mb-4 sm:mb-2">
                <p className="text-gray-600 font-bold text-sm md:text-lg">Sign in to your account</p>
              </div>
              {message ? (
                <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 animate-fade-in text-sm">
                  {message}
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                <div>
                  <label className="block text-sm md:text-base font-medium text-gray-700 mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm md:text-base font-medium text-gray-700 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={e =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                  {error && <p className="mt-2 text-sm md:text-base text-red-600">{error}</p>}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={e =>
                        setFormData({ ...formData, rememberMe: e.target.checked })
                      }
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm md:text-base text-gray-600">
                      Remember me
                    </span>
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-sm md:text-base text-blue-600 hover:text-blue-800"
                  >
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 text-sm md:text-base font-medium"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>

                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="flex w-full items-center justify-center mx-auto">
                  <GoogleOAuthButton />
                </div>

                <p className="text-center text-sm md:text-base text-gray-600">
                  Don't have an account?{' '}
                  <a
                    href="/register"
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sign up
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
